import type { AddSubtitleDraftParams } from './types';

/** SRT-like "HH:mm:ss,SSS" → milliseconds（与 videoEdit 内原逻辑一致） */
export function parseSubtitleTimeToMs(time: string): number {
    const [hms, ms = '0'] = (time || '00:00:00,000').split(',');
    const [h = '0', m = '0', s = '0'] = hms.split(':');
    return Number(h) * 3600000 + Number(m) * 60000 + Number(s) * 1000 + Number(ms);
}

export function getLocalInsertBlockReason(
    subtitles: Task.SubtitlesLegacy[],
    params: AddSubtitleDraftParams,
    rawInsertIndex: number
): string | null {
    if (parseSubtitleTimeToMs(params.origin_start_time) >= parseSubtitleTimeToMs(params.origin_end_time)) {
        return '当前台词后已无时间空隙，无法新增台词';
    }
    const hasSameTempSubtitle = subtitles.some(
        (sub) =>
            sub.id < 0 &&
            sub.origin_start_time === params.origin_start_time &&
            sub.origin_end_time === params.origin_end_time
    );
    if (hasSameTempSubtitle) {
        return '该台词后已存在临时台词占位，不能重复新增';
    }
    if (rawInsertIndex >= 0) {
        const nextSubtitle = subtitles[rawInsertIndex + 1];
        if (nextSubtitle && nextSubtitle.id < 0) {
            return '该台词后已新增过一次台词，不能重复新增';
        }
    }
    return null;
}

export function normalizeLocalInsertIndex(
    subtitles: Task.SubtitlesLegacy[],
    rawInsertIndex: number
): number {
    if (rawInsertIndex === -1 && subtitles.length > 0) {
        return subtitles.length - 1;
    }
    return rawInsertIndex;
}

export function spliceSubtitleAfterIndex(
    subtitles: Task.SubtitlesLegacy[],
    insertIndex: number,
    newSubtitle: Task.SubtitlesLegacy
): Task.SubtitlesLegacy[] {
    if (insertIndex >= 0) {
        return [
            ...subtitles.slice(0, insertIndex + 1),
            newSubtitle,
            ...subtitles.slice(insertIndex + 1),
        ];
    }
    return [...subtitles, newSubtitle];
}

export function buildTemporarySubtitle(
    taskId: number,
    params: AddSubtitleDraftParams,
    tempId: number,
    currentSubtitle: Task.SubtitlesLegacy | null | undefined
): Task.SubtitlesLegacy {
    return {
        id: tempId,
        task_id: taskId,
        origin_text: params.origin_text,
        post_text: '',
        post_start_time: params.post_start_time,
        post_end_time: params.post_end_time,
        origin_start_time: params.origin_start_time,
        origin_end_time: params.origin_end_time,
        is_episode_voice: false,
        origin_audio: '',
        trans_audio: '',
        subtitle_id: 0,
        audio_speed: 1.0,
        volume_gain_db: 1,
        speakers: {
            id: currentSubtitle?.speakers?.id || params.speaker_id,
            series_id: currentSubtitle?.speakers?.series_id || 0,
            task_id: taskId,
            speaker_name: currentSubtitle?.speakers?.speaker_name || '新说话人',
            description: currentSubtitle?.speakers?.description || '',
            is_series_speaker: currentSubtitle?.speakers?.is_series_speaker || false,
        },
        voice_info: {
            origin_text: '',
            origin_start_time: '',
            origin_end_time: '',
            origin_audio: '',
        },
        words: [],
    };
}

/** 解析创建新字幕时作为锚点的真实字幕 id（必须 > 0） */
export function resolveCreateSubtitleAnchorId(
    subtitles: Task.SubtitlesLegacy[],
    currentSubtitleId?: number
): { anchorId: number } | { error: string } {
    if (!subtitles.length) {
        return { error: '无法创建字幕：没有可用的字幕ID' };
    }
    const realSubtitles = subtitles.filter((sub) => sub.id > 0);
    if (realSubtitles.length === 0) {
        return { error: '无法创建字幕：没有可用的真实字幕ID' };
    }

    let subtitleIdToUse = realSubtitles[realSubtitles.length - 1].id;

    if (currentSubtitleId !== undefined) {
        if (currentSubtitleId > 0) {
            subtitleIdToUse = currentSubtitleId;
        } else {
            const tempSubtitleIndex = subtitles.findIndex((sub) => sub.id === currentSubtitleId);
            if (tempSubtitleIndex > 0) {
                let found = false;
                for (let i = tempSubtitleIndex - 1; i >= 0; i--) {
                    if (subtitles[i].id > 0) {
                        subtitleIdToUse = subtitles[i].id;
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    subtitleIdToUse = realSubtitles[0].id;
                }
            } else {
                subtitleIdToUse = realSubtitles[0].id;
            }
        }
    } else {
        subtitleIdToUse = realSubtitles[realSubtitles.length - 1].id;
    }

    return { anchorId: subtitleIdToUse };
}

export function mapCreatedSubtitleToLegacy(
    newSubtitleData: Task.Subtitles,
    newSubtitleParams: AddSubtitleDraftParams
): Task.SubtitlesLegacy {
    return {
        id: newSubtitleData.id,
        task_id: newSubtitleData.taskId,
        origin_text: newSubtitleData.originText,
        post_text: newSubtitleData.postText || '',
        post_start_time: newSubtitleParams.post_start_time || newSubtitleData.postStartTime || '',
        post_end_time: newSubtitleParams.post_end_time || newSubtitleData.postEndTime || '',
        origin_start_time: newSubtitleData.originStartTime,
        origin_end_time: newSubtitleData.originEndTime,
        origin_post_time: newSubtitleData.originPostTime,
        is_episode_voice: newSubtitleData.isEpisodeVoice || false,
        origin_audio: newSubtitleData.originAudio || '',
        trans_audio: newSubtitleData.transAudio || '',
        subtitle_id: newSubtitleData.subtitleId || newSubtitleData.id,
        audio_speed: newSubtitleData.audioSpeed || 1.0,
        volume_gain_db: newSubtitleData.volumeGainDb || 1.0,
        speakers: {
            id: newSubtitleData.speaker.id,
            series_id: newSubtitleData.speaker.seriesId,
            task_id: newSubtitleData.taskId,
            speaker_name: newSubtitleData.speaker.speakerName,
            description: newSubtitleData.speaker.description || '',
            is_series_speaker: newSubtitleData.speaker.isSeriesSpeaker,
        },
        voice_info: {
            origin_text: newSubtitleData.originText,
            origin_start_time: newSubtitleData.originStartTime,
            origin_end_time: newSubtitleData.originEndTime,
            origin_audio: newSubtitleData.originAudio,
        },
        words:
            newSubtitleData.words?.map((word) => ({
                id: word.id,
                subtitle_id: word.subtitleId,
                position: word.position,
                origin_char: word.originChar,
                start_ms: word.startMs,
                end_ms: word.endMs,
            })) || [],
    };
}

export function replaceTempOrAppendSubtitle(
    subtitles: Task.SubtitlesLegacy[],
    newSubtitle: Task.SubtitlesLegacy,
    params: AddSubtitleDraftParams
): Task.SubtitlesLegacy[] {
    const tempSubtitleIndex = subtitles.findIndex(
        (sub) =>
            sub.id < 0 &&
            sub.origin_start_time === params.origin_start_time &&
            sub.origin_end_time === params.origin_end_time
    );
    if (tempSubtitleIndex >= 0) {
        return [
            ...subtitles.slice(0, tempSubtitleIndex),
            newSubtitle,
            ...subtitles.slice(tempSubtitleIndex + 1),
        ];
    }
    return [...subtitles, newSubtitle];
}
